import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import moment from 'moment';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { CertificateResource } from '../../../resources/certificate.resource';
import { Subject, Subscription } from 'rxjs';
import { Csr, DistinguishedNameProperties } from '../../../../shared/models/generated/ziroModels';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { ZiroFormsUpdate, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import { GenerateCsrFormComponent } from './generate-csr-form.component';
import { ZiroFormStateService } from '../../../../forms/ziro-form-state.service';

@Component({
  selector: 'ziro-admin-generate-csr',
  templateUrl: './generate-csr.component.html',
  standalone: false,
})
export class GenerateCsrComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  private certificateResource = inject(CertificateResource);
  private sanitizer = inject(DomSanitizer);
  private ziroFormStateService = inject(ZiroFormStateService);

  readonly csrTimestamp = input<Subject<string>>();
  readonly displayCsr = input<Subject<boolean>>();
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() existingCsr: Csr;

  readonly csrGenerated = output<Csr>();

  @ViewChild(GenerateCsrFormComponent) csrFormComponent: GenerateCsrFormComponent;

  distinguishedNameProperties = {} as DistinguishedNameProperties;
  isGeneratingCsr = false;
  csrForm: ZiroFormsUpdate<DistinguishedNameProperties>;
  formValidator = new EventEmitter<boolean>();

  fileUrl: SafeUrl;
  fileName: string;

  ziroIcons = ZiroIcons;

  shouldDisplayCsr = false;

  private _subscriptions = new Subscription();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.existingCsr && !changes.existingCsr.currentValue) {
      this.displayCsr().next(false);
    }
  }

  ngOnInit(): void {
    this.distinguishedNameProperties.subjectAlternativeNames = [];
    this.certificateResource.getCsrs().subscribe((csrs) => this.updateCsr(csrs));

    const sub = this.displayCsr()
      .asObservable()
      .subscribe((state: boolean) => {
        this.shouldDisplayCsr = state;
      });
    this._subscriptions.add(sub);
  }

  ngAfterViewInit(): void {
    this.csrFormComponent.formSubmit$.subscribe(() => {
      this.generateCsr();
    });
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  setDisplayCsr(display: boolean): void {
    this.displayCsr().next(display);
  }

  buildFileUrl(csr: string): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(`data:text/plain;charset=utf8,${encodeURIComponent(csr)}`);
  }

  buildFileName(timestamp: string): string {
    return timestamp ? `ziro_${timestamp.replace(' ', '-')}.csr` : 'ziro.csr';
  }

  onFormUpdate($event: ZiroFormsUpdate<DistinguishedNameProperties>): void {
    this.csrForm = $event;
  }

  generateCsr(): void {
    this.formValidator.emit(true);
    if (this.csrForm.valid === ZiroFormsValidationState.INVALID) {
      return;
    }
    this.isGeneratingCsr = true;
    this.certificateResource.postCsr(this.csrForm.new).subscribe((csrs) => {
      this.updateCsr(csrs);
      this.isGeneratingCsr = false;
      this.ziroFormStateService.setIsFormDirty(false);
    });
  }

  updateCsr(csrs: Csr[]): void {
    const csr = csrs[0];
    this.existingCsr = csr;

    if (csr) {
      this.buildCsrTimestamp(csr.timestamp);
      this.fileUrl = this.buildFileUrl(csr.csr);
      this.fileName = this.buildFileName(csr.timestamp);
      this.displayCsr().next(true);
      this.isGeneratingCsr = false;
      this.csrGenerated.emit(csr);
    }
  }

  buildCsrTimestamp(rawTimestamp: string): void {
    const dateUtc = moment.utc(rawTimestamp.replace('.', ':'));
    const timestamp = moment(dateUtc).local().format('YYYY-MM-DD HH:mm');
    this.csrTimestamp().next(timestamp);
  }
}
